import React, { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Input,
    Form,
} from "reactstrap";
import DataTable from "react-data-table-component";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import Spinner from "../../Components/Common/Spinner";
import NoDataFound from "../../Components/Common/NoDataFound";
import { customStyles } from "../../Constant";
import "react-toastify/dist/ReactToastify.css";

const PAGE_SIZES = [10, 25, 50, 100];

const JOBSEEKER_STATUS_OPTIONS = [
    { value: "BLOCKED", label: "Blocked" },
    { value: "PENDING", label: "Pending" },
    { value: "ACTIVE", label: "Active" },
];

const ManageJobseekers = () => {
    const [jobseekers, setJobseekers] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [updatingStatusId, setUpdatingStatusId] = useState(null);

    const animatedComponents = makeAnimated();
    const selectColors = {
        primary: "#007bff",
        primary25: "#cce5ff",
        approved: "green",
        rejected: "red",
        hold: "orange",
        lightGray: "#f8f9fa",
        darkGray: "#343a40",
    };
    const getStatusColor = (value) => {
        switch (value) {
            case "ACTIVE": return selectColors.approved;
            case "PENDING": return selectColors.hold;
            case "BLOCKED": return selectColors.rejected;
            default: return selectColors.darkGray;
        }
    };
    const customStylesSelect = {
        control: (provided, state) => ({
            ...provided,
            minWidth: "120px",
            borderColor: state.isFocused ? selectColors.primary : provided.borderColor,
            boxShadow: state.isFocused ? `0 0 0 1px ${selectColors.primary}` : provided.boxShadow,
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: getStatusColor(state.data?.value),
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? getStatusColor(state.data.value) : state.isFocused ? selectColors.primary25 : selectColors.lightGray,
            color: state.isSelected || state.isFocused ? "#fff" : selectColors.darkGray,
        }),
    };

    const fetchJobseekers = useCallback(async (page, perPageSize, search, sortBy, sortDir) => {
        setLoading(true);
        setError(null);
        try {
            const res = await AxiosInstance().post("/admin/fetch_all_jobseekers", {
                page,
                per_page: perPageSize,
                search: (search || "").trim() || undefined,
                sort_by: sortBy || "created_at",
                sort_dir: sortDir || "desc",
            });
            setJobseekers(Array.isArray(res.data?.data) ? res.data.data : []);
            setTotalRows(typeof res.data?.total === "number" ? res.data.total : 0);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load jobseekers");
            setJobseekers([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchJobseekers(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [currentPage, perPage, searchApplied, sortColumn, sortDirection, fetchJobseekers]);

    const loadData = useCallback(() => {
        fetchJobseekers(currentPage, perPage, searchApplied, sortColumn, sortDirection);
    }, [fetchJobseekers, currentPage, perPage, searchApplied, sortColumn, sortDirection]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const handleStatusChange = async (seekerId, newStatus) => {
        setUpdatingStatusId(seekerId);
        try {
            const res = await AxiosInstance().post("/admin/update_jobseeker_status", {
                seeker_id: seekerId,
                status: newStatus,
            });
            if (res.data.status) {
                toast.success(res.data.message || "Status updated");
                fetchJobseekers(currentPage, perPage, searchApplied, sortColumn, sortDirection);
            } else {
                toast.error(res.data.message || "Update failed");
            }
        } catch (e) {
            toast.error(e.response?.data?.message || "Update failed");
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        setSearchApplied(searchQuery);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => setCurrentPage(page);
    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (selectedColumn, direction) => {
        const field = selectedColumn?.id || "created_at";
        setSortColumn(field);
        setSortDirection(direction === "asc" ? "asc" : "desc");
        setCurrentPage(1);
    };

    const tableColumns = [
        {
            name: "#",
            cell: (row, index) => (currentPage - 1) * perPage + index + 1,
            width: "60px",
            sortable: false,
        },
        {
            id: "jobseeker_full_name",
            name: "Name",
            selector: (row) => row.jobseeker_full_name || "—",
            sortable: true,
            grow: 1.5,
            cell: (row) => <span className="fw-medium">{row.jobseeker_full_name || "—"}</span>,
        },
        {
            id: "jobseeker_email1",
            name: "Email",
            selector: (row) => row.jobseeker_email1 || "—",
            sortable: true,
            grow: 1.5,
        },
        {
            id: "jobseeker_phone1",
            name: "Phone",
            selector: (row) => row.jobseeker_phone1 || "—",
            sortable: true,
            width: "120px",
        },
        {
            id: "jobseeker_location",
            name: "Location",
            selector: (row) => row.jobseeker_location || "—",
            sortable: true,
            grow: 1.5,
        },
        {
            name: "Status",
            width: "190px",
            center: true,
            cell: (row) => {
                const currentStatus = (row.status || "").toUpperCase();
                const valueOption = JOBSEEKER_STATUS_OPTIONS.find((opt) => opt.value === currentStatus) || { value: currentStatus || "", label: currentStatus || "—" };
                return (
                    <Select
                        components={animatedComponents}
                        className="basic-single"
                        classNamePrefix="select"
                        value={valueOption}
                        onChange={(selectedOption) => selectedOption && handleStatusChange(row.seeker_id, selectedOption.value)}
                        options={JOBSEEKER_STATUS_OPTIONS}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                            ...customStylesSelect,
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                        }}
                        isDisabled={updatingStatusId === row.seeker_id}
                        isSearchable={false}
                        menuPlacement="auto"
                    />
                );
            },
        },
        {
            id: "created_at",
            name: "Registered",
            selector: (row) => row.jobseeker_registration_date || row.created_at || "—",
            sortable: true,
            width: "110px",
            cell: (row) => (
                <span className="text-muted small">
                    {formatDate(row.jobseeker_registration_date || row.created_at)}
                </span>
            ),
        },
    ];

    if (loading && jobseekers.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Jobseekers" mainTitle="Admin" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner />
                </div>
            </Fragment>
        );
    }

    if (error && jobseekers.length === 0) {
        return (
            <Fragment>
                <Breadcrumbs title="Jobseekers" mainTitle="Admin" />
                <Row>
                    <Col xs="12">
                        <Card>
                            <CardBody className="text-center py-5">
                                <p className="text-danger mb-0">{error}</p>
                                <Button color="primary" className="mt-3" onClick={loadData}>
                                    Retry
                                </Button>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Jobseekers" mainTitle="Admin" />
            <Row>
                <Col xs="12" className="box-col-12 xl-100">
                    <Card>
                        <CardHeader className="py-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
                                <Form
                                    className="theme-form me-2 flex-grow-1"
                                    onSubmit={handleSearchSubmit}
                                >
                                    <div
                                        className="input-group flex-nowrap"
                                        style={{ height: 35, maxWidth: "350px" }}
                                    >
                                        <Input
                                            className="form-control"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            type="search"
                                            placeholder="Search by name, email, phone or location..."
                                        />
                                        <Button
                                            color="primary"
                                            type="submit"
                                            className="btn-primary input-group-text pe-3 ps-3"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </Form>
                                <div className="d-flex align-items-center ms-auto mt-3 mt-md-0">
                                    <Button color="primary" outline onClick={loadData} className="d-flex align-items-center">
                                        <i className="fa fa-sync-alt me-1" /> Refresh
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <CardBody className="px-0 py-3">
                            <div className="table-container">
                                <DataTable
                                    responsive
                                    customStyles={customStyles}
                                    data={jobseekers}
                                    columns={tableColumns}
                                    striped
                                    pagination
                                    paginationServer
                                    sortServer
                                    onSort={handleSort}
                                    defaultSortFieldId="created_at"
                                    defaultSortAsc={false}
                                    paginationTotalRows={totalRows}
                                    paginationPerPage={perPage}
                                    paginationRowsPerPageOptions={PAGE_SIZES}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    progressPending={loading}
                                    progressComponent={<Spinner />}
                                    noDataComponent={<NoDataFound message="No jobseekers found" />}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <ToastContainer />
        </Fragment>
    );
};

export default ManageJobseekers;
